import { FC, ReactNode, useEffect, useRef, useState, lazy } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState, convertFromHTML } from 'draft-js';
// import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import { Interweave } from 'interweave';
// import TiptapEditor from './TipTapEditor';
import { useReactToPrint } from 'react-to-print';
import { Client, Provider } from '../../../helpers/models';
import ErrorBoundary from './ErrorBoundary';
import type { TiptapEditorHandle } from './TipTapEditor';
import { useAlert } from 'react-alert';

const TiptapEditorLazy = lazy(() => import('./TipTapEditor'));
const EditorLazy = lazy(() => import('react-simple-wysiwyg').then((module) => ({ default: module.Editor })));
const EditorProvider = lazy(() =>
  import('react-simple-wysiwyg').then((module) => ({ default: module.EditorProvider }))
);

type Props = {
  content?: string;
  editMode: boolean;
  placeholder?: string | ReactNode;
  className?: string;
  formattingOptions?: string[];
  contentRef?: React.RefObject<HTMLDivElement>;
  onChange: (newValue: string) => void;
  provider?: Provider;
  client?: Client;
  /** Read the live editor content (edit mode only); onChange is debounced and lags. */
  editorRef?: React.Ref<TiptapEditorHandle>;
};

const HtmlInput: FC<Props> = (props) => {
  const alert = useAlert();
  const [regExSupported, setRegExSupported] = useState(false);
  const isContentEmpty =
    !props.content || props.content.length == 0 || ['<p></p>\n', '<p></p>', '<p><br></p>'].indexOf(props.content) != -1;

  // these are used for fallback editor
  const [html, setHtml] = useState(props.content);
  useEffect(() => {
    setHtml(props.content);
  }, [props.content]);
  // end of fallback editor

  useEffect(() => {
    try {
      const regex = new RegExp('(?<=invalid)');
      setRegExSupported(true);
    } catch (error) {
      console.error('Advanced editor is not supported on this browser.');
    }
  }, []);

  if (regExSupported) {
    return props.editMode ? (
      <div className={props.className || ''}>
        <ErrorBoundary fallback={<></>}>
          <TiptapEditorLazy
            ref={props.editorRef}
            content={props.content}
            onChange={(newContent) => {
              props.onChange?.(newContent);
            }}
            provider={props.provider}
            client={props.client}
          />
        </ErrorBoundary>
      </div>
    ) : isContentEmpty ? (
      <div className={props.className || 'providerNotes'}>{props.placeholder}</div>
    ) : (
      // <Interweave content={props.content} className={(props.className || 'providerNotes') + ' tiptap'} />
      <>
        <div className={props.className + ' disabled-html-editor'} ref={props?.contentRef}>
          <ErrorBoundary fallback={<></>}>
            <TiptapEditorLazy
              content={props.content}
              onChange={(newContent) => {
                props.onChange?.(newContent);
              }}
              disabled={true}
              provider={props.provider}
              client={props.client}
            />
          </ErrorBoundary>
        </div>
      </>
    );
  } else {
    if (props.editMode) {
      return (
        <EditorProvider>
          <EditorLazy
            value={html}
            onChange={(e) => props.onChange?.(e.target.value)}
            className={(props.className || 'providerNotes') + ' tiptap'}
          />
        </EditorProvider>
      );
    } else {
      return <Interweave content={props.content} className={(props.className || 'providerNotes') + ' tiptap'} />;
    }
  }
};

export { HtmlInput };
